import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import InfoIcon from "@mui/icons-material/Info";
import { roomFeaturesTooltip } from "../../../constants/TooltipInformation";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const RoomFeatures = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [roomFeatureCode, setRoomFeatureCode] = useState<string | null>(null);
  const [roomFeatureCodeError, setRoomFeatureCodeError] =
    useState<boolean>(false);
  const [roomFeatureName, setRoomFeatureName] = useState<string | null>(null);
  const [roomFeatureNameError, setRoomFeatureNameError] =
    useState<boolean>(false);
  const [roomFeatureInformation, setRoomFeatureInformation] =
    useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [active, setActive] = useState<boolean>(true);
  const [isCondo, setIsCondo] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const roomFeaturePayload = {
    code: roomFeatureCode,
    name: roomFeatureName,
    isActive: active ?? true,
    isCondoOnly: isCondo ?? false,
  };

  const addRoomFeature = () => {
    if (validateInput()) {
      if (
        !roomFeatureCode ||
        roomFeatureCode === null ||
        roomFeatureCode === ""
      ) {
        setRoomFeatureCodeError(true);
      }
      if (
        !roomFeatureName ||
        roomFeatureName === null ||
        roomFeatureName === ""
      ) {
        setRoomFeatureNameError(true);
      } else if (
        checkDuplicate(
          Array.isArray(roomFeatureInformation) ? roomFeatureInformation : [],
          DNDFieldsEnum.ROOMFEATURECODE,
          roomFeatureCode,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setRoomFeatureInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...roomFeaturePayload,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...roomFeaturePayload,
                  },
                ],
          );
        });
        setLastUpdatedRecord(roomFeaturePayload);
        editRoomFeature(null);
        setEditClicked(false);
      }
    }
  };

  const editRoomFeature = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setRoomFeatureCode(list?.code);
    setRoomFeatureName(list?.name);
    setIsCondo(list?.isCondoOnly);
    setActive(list ? list.isActive : true);
  };

  const updateRoomFeature = () => {
    if (!validateInput()) return;
    if (
      !roomFeatureCode ||
      roomFeatureCode === null ||
      roomFeatureCode === ""
    ) {
      setRoomFeatureCodeError(true);
      return;
    }
    if (
      !roomFeatureName ||
      roomFeatureName === null ||
      roomFeatureName === ""
    ) {
      setRoomFeatureNameError(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(roomFeatureInformation)
          ? roomFeatureInformation
          : []
        ).filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.ROOMFEATURECODE,
        roomFeatureCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRoomFeatureInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...roomFeaturePayload,
              };
            }
            return item;
          })
        : prev,
    );
    editRoomFeature(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.roomFeatures} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteRoomFeature = (id: number) => {
    setRoomFeatureInformation(deleteItem(roomFeatureInformation, id));
  };

  const validateInput = () => {
    setRoomFeatureCodeError(false);
    setRoomFeatureNameError(false);
    let isValid = true;
    if (
      roomFeatureCode &&
      !validateString(
        roomFeatureCode,
        validationLength.roomFeatureCode,
        validationType.CODE,
      )
    ) {
      setRoomFeatureCodeError(true);
      isValid = false;
    }
    if (
      roomFeatureName &&
      !validateString(
        roomFeatureName,
        validationLength.roomFeatureName,
        validationType.OTHER,
      )
    ) {
      setRoomFeatureNameError(true);
      isValid = false;
    }
    return isValid;
  };

  useEffect(() => {
    data(roomFeatureInformation);
    editClicked &&
      setSelectedId(
        checkData(roomFeatureInformation, selectedId) ? selectedId : null,
      );
  }, [roomFeatureInformation]);

  useEffect(() => {
    fieldValues &&
      setRoomFeatureInformation(
        Array.isArray(fieldValues)
          ? fieldValues
          : fieldValues
            ? [fieldValues]
            : null,
      );
  }, []);

  useEffect(() => {
    validateInput();
  }, [roomFeatureCode, roomFeatureName]);

  const roomFeatureCodeHelperText = roomFeatureCodeError
    ? !(roomFeatureCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.roomFeatures.roomFeatureCode
    : versaFieldHelperText.roomFeatures.roomFeatureCode;

  const roomFeatureNameHelperText = roomFeatureNameError
    ? !(roomFeatureName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.roomFeatures.roomFeatureName
    : versaFieldHelperText.roomFeatures.roomFeatureName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Room Feature Information
              <Tooltip title={tooltipComponent(roomFeaturesTooltip.header)}>
                <InfoIcon />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editRoomFeature(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRoomFeature(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Room Feature Code
                <Tooltip
                  title={tooltipComponent(roomFeaturesTooltip.featureCode)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="roomFeatureCode"
                variant="outlined"
                placeholder="Room Feature Code"
                helperText={roomFeatureCodeHelperText}
                error={roomFeatureCodeError}
                value={roomFeatureCode ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setRoomFeatureCode(e.target.value.toUpperCase());
                  setRoomFeatureCodeError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Room Feature Name
                <Tooltip
                  title={tooltipComponent(roomFeaturesTooltip.featureName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="roomFeatureName"
                variant="outlined"
                placeholder="Room Feature Name"
                helperText={roomFeatureNameHelperText}
                error={roomFeatureNameError}
                value={roomFeatureName ?? ""}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setRoomFeatureName(e.target.value);
                  setRoomFeatureNameError(false);
                }}
              />
            </Stack>
          </Stack>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            width="100%"
            gap="50px"
          >
            <Stack direction="row" gap="2px">
              <FormControlLabel
                control={
                  <Switch
                    checked={active}
                    onChange={() => setActive(!active)}
                  />
                }
                label="Active?"
              />
              {/* <Tooltip title={tooltipComponent("")}>
                <InfoIcon fontSize="small" />
              </Tooltip> */}
            </Stack>
            <Stack direction="row" gap="2px">
              <FormControlLabel
                control={
                  <Switch
                    id="CondoOnlySwitch"
                    checked={isCondo}
                    onChange={() => setIsCondo(!isCondo)}
                  />
                }
                label="Condo Only?"
              />
              {/* <Tooltip title={tooltipComponent("")}>
                <InfoIcon fontSize="small" />
              </Tooltip> */}
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateRoomFeature : addRoomFeature}
              id="addNewRoomFeature"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} New Room
              Feature
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Room Features Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {roomFeatureInformation ? (
                <DraggableItemList
                  itemData={roomFeatureInformation}
                  onOrderUpdate={(order) => setRoomFeatureInformation(order)}
                  onEdit={(info) => {
                    editRoomFeature(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRoomFeature(id)}
                  code={DNDFieldsEnum.ROOMFEATURECODE}
                  name={DNDFieldsEnum.ROOMFEATURENAME}
                />
              ) : (
                <Typography variant="body2">
                  No Room features Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RoomFeatures;
